import { call, getToken } from './api.js';

/* ══════════════════════════════════════════════════════════════════════════
 * TRACKING — THE NUMBERS ELEAT SELLS
 *
 * ⚠️ `POST /analytics/event` HAS EXISTED THE WHOLE TIME AND NOTHING EVER
 * CALLED IT. Every screen in the Eleater app was built, connected and shipped
 * without sending a single event. So every figure a restaurant would ever be
 * shown — how many people opened their page, looked at their Happy Hour,
 * searched and found them — is zero. Not because nobody looked. Because
 * nobody counted.
 *
 * That is not a missing feature. It is the product Eleat sells to a
 * restaurant, switched off.
 *
 * ── ⚠️ THE VOCABULARY IS CLOSED, AND THAT IS THE POINT ──────────────────────
 *
 * `analytics_events.event_name` is free text in the database with no enum and
 * no CHECK. The route is the gate: `svc.EVENTS` is the single list, and an
 * unknown name is refused at the door rather than stored and silently lost.
 * The route's own comment explains what that prevents — *"`menu_open`,
 * `menuOpened` and `menu-opened` becoming three separate events that each hold
 * a slice of the truth and none of which ever appear in a number again."*
 *
 * ⚠️ SO THE NINE NAMES BELOW ARE COPIED FROM `event-registry.js` AND NOTHING
 * ELSE MAY BE SENT. A tenth invented here is a 400 at best and a number
 * nobody can find at worst.
 *
 * ── AND EVERY EVENT HAS REQUIRED FIELDS ─────────────────────────────────────
 *
 * ⚠️ `deal_viewed` WITHOUT A `deal_id` IS A ROW THAT COUNTS NOTHING. The
 * registry lists what each event must carry, and this file mirrors it so a
 * screen cannot fire a half-empty event and have it accepted. The check runs
 * HERE as well as on the server, because a rejected event is a lost number and
 * the screen is where the fields actually are.
 *
 * ── ⚠️ IT NEVER GETS IN THE WAY ─────────────────────────────────────────────
 *
 * Fire and forget. Never awaited, never surfaced, never retried. An Eleater
 * looking at a menu must not wait on Eleat's bookkeeping, and a failed event
 * must never put an error in front of somebody. If tracking breaks, Eleat gets
 * worse numbers; the Eleater notices nothing. That is the correct trade and it
 * is deliberate.
 * ══════════════════════════════════════════════════════════════════════════ */

/* the nine, from event-registry.js */
export const EVENTS = [
  'restaurant_viewed', 'deal_viewed', 'menu_opened', 'offer_opened',
  'category_click', 'search', 'zaryna_query', 'feed_viewed', 'share',
  'game_day_viewed', 'game_day_opened',
  'directions_tapped', 'call_tapped',
];

/* what each one must carry, from the registry's REQUIRED map */
const REQUIRED = {
  restaurant_viewed: ['restaurant_id'],
  deal_viewed: ['restaurant_id', 'deal_id'],
  menu_opened: ['restaurant_id'],
  offer_opened: ['restaurant_id', 'deal_id'],
  category_click: ['metadata.category'],
  search: ['search_query'],
  zaryna_query: [],
  feed_viewed: ['restaurant_id'],
  share: ['restaurant_id', 'metadata.share_target'],
  game_day_viewed: ['restaurant_id'],
  game_day_opened: ['restaurant_id'],
  directions_tapped: ['restaurant_id'],
  call_tapped: ['restaurant_id'],
};

function has(body, path) {
  if (!path.includes('.')) return body[path] != null && body[path] !== '';
  const [a, b] = path.split('.');
  return body[a] && body[a][b] != null && body[a][b] !== '';
}

/**
 * Send one event. Returns nothing; throws nothing.
 *
 * ⚠️ `platform: 'web'` ON EVERY EVENT. The column takes whatsapp | web | qr,
 * and the React app is the web one. Leaving it null would make every number
 * unattributable the day the WhatsApp door opens and both are running.
 */
export function track(name, body = {}) {
  if (!EVENTS.includes(name)) {
    /* ⚠️ DROPPED IN THE APP RATHER THAN SENT AND REFUSED. A 400 in a phone's
       network tab is nobody's job to read. */
    return;
  }
  const need = REQUIRED[name] || [];
  if (!need.every((p) => has(body, p))) return;

  try {
    call('/analytics/event', {
      method: 'POST',
      /* ⚠️ AUTH IS OPTIONAL ON THIS ROUTE AND THAT IS DELIBERATE. Browsing is
         open to everyone — Help says so — so a signed-out Eleater looking at a
         venue is real demand and counts. The token is attached when there is
         one so the event can be attributed, and omitted when there is not. */
      auth: !!getToken(),
      /* ⚠️⚠️ `event_name` — THE ONE FIELD THE ROUTE REQUIRES, AND IT WAS NEVER
       * SENT. 23 September 2026.
       *
       * This built `{ ...body, platform: 'web' }`. The event's NAME — the whole
       * point of the call — was checked against the registry three lines above
       * and then dropped on the floor. Every event Eleat has ever fired came
       * back **422 Validation failed: event_name Required**, and the `.catch`
       * below swallowed it because "a lost number is never the Eleater's
       * problem".
       *
       * So the file at the top of this module says *"nothing ever sent a single
       * event… every figure a restaurant would ever be shown is zero"* — and
       * that was still true after the file was written to fix it. Nothing was
       * counted: not a menu opened, not a deal viewed, not a search, not a
       * reservation click. **The number a restaurant pays to see.**
       *
       * ⚠️ AND IT WAS INVISIBLE BY DESIGN. Tracking is deliberately silent so a
       * failed event never interrupts somebody looking at a menu. Correct — and
       * it means nothing on any screen could ever have shown this. It took
       * opening the phone's own console to see 78 rejections on one screen. */
      body: { ...body, event_name: name, platform: 'web' },
    }).catch(() => { /* a lost number is never the Eleater's problem */ });
  } catch { /* nor is a thrown one */ }
}


/**
 * A HOME RAIL AS IT WAS DRAWN — 3 Oct 2026. Which venues were shown, in which
 * order, on which rail: the "times shown" behind Restaurant Analytics'
 * carousel page (`carousel_impressions`, migration 034). One call per rail
 * per order, never per card; throws nothing.
 */
const SHOWN = new Set();
export function shown(rail, ids) {
  const list = (ids || []).filter(Boolean).slice(0, 30);
  if (!rail || !list.length) return;
  const key = rail + ':' + list.join(',');
  if (SHOWN.has(key)) return;
  SHOWN.add(key);
  try {
    call('/analytics/carousel-shown', { method: 'POST', auth: !!getToken(), body: { rail, ids: list } })
      .catch(() => {});
  } catch (e) { /* never in the way of the screen */ }
}
